import { useState, forwardRef, useCallback } from 'react';
import { prefetchPage } from 'prefetcher/utils/prefetchPage';
import ErrorBoundary from '../../../components/ErrorBoundary';
import { useTranslation } from '../../../hooks/useNavConfig';
import { useHelpNotification } from '../../../hooks/useHelpNotification';
import NewHelpIcon from '../../../components/icons/HelpIcon';
import { trackHelpNotificationDismiss } from '../../../components/GlobalToolbar/utils/trackHelpNotification';
import { HelpIconTooltip } from './HelpIconTooltip';
import { useHelpTooltip } from '../../../components/GlobalToolbar/hooks/useHelpTooltip';
import { useToggleHelpWidget } from '../../../external-contrib/in-app-help/hooks/useToggleHelpWidget';
import { get, set } from '../../../utils/tempStorage';
import { HAS_OPENED_WIDGET_OR_DISMISSED_TOOLTIP_KEY } from '../../../external-contrib/in-app-help/constants/tooltips';
import { useAutoOpen } from '../../../external-contrib/in-app-help/hooks/useAutoOpen';
import { useDndWidget } from '../../../external-contrib/common/hooks/useDndWidget';
import { useAddZorseFunctions } from '../../../external-contrib/in-app-help/hooks/useAddZorseFunctions';
import { useOpenUrl } from '../../../external-contrib/in-app-help/hooks/useOpenUrl';
import useHelpCsatSurvey from '../../../external-contrib/in-app-help/hooks/useHelpCsatSurvey';
import useHelpModal from '../../../external-contrib/in-app-help/hooks/useHelpModal';
import useObserveHtmlClass from '../../../external-contrib/in-app-help/hooks/useObserveHtmlClass';
import { StyledHelpButton } from './HelpButton';
import memoWithDisplayName from '../../../utils/memoWithDisplayName';
import { NotificationsIndicator } from './HelpButton';
import { IN_APP_HELP_COMPONENT } from 'navigation-components/config';
import { useTheme } from 'foundations-theming/utils';
import { navTheme } from '../../../theme/nav-theme';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const HELP_CLOSE = 'close';
export const HELP_MINIMIZE = 'minimize';
export const HELP_RESTORE = 'restore';
const prefetchHelpWidget = () => prefetchPage('in-app-help-ui-prefetch');
export const Help = memoWithDisplayName('Help', /*#__PURE__*/forwardRef(({
  id,
  tabIndex,
  onKeyDown
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const [showNotification, setShowNotification] = useHelpNotification();
  useDndWidget(IN_APP_HELP_COMPONENT);
  useOpenUrl();
  const {
    cancelCsatSurvey
  } = useHelpCsatSurvey();
  useHelpModal();
  const {
    showTooltip: shouldRenderHelpTooltip,
    closeHelpTooltip
  } = useHelpTooltip();
  const onToggleWidget = useCallback(open => {
    const handleCloseTooltips = () => {
      if (closeHelpTooltip) {
        closeHelpTooltip();
      }
    };
    if (open) {
      if (get(HAS_OPENED_WIDGET_OR_DISMISSED_TOOLTIP_KEY) !== 'true') {
        set(HAS_OPENED_WIDGET_OR_DISMISSED_TOOLTIP_KEY, 'true');
        handleCloseTooltips();
      }
      // attempt to cancel scheduled CSAT survey on widget open/reopen
      if (cancelCsatSurvey) {
        cancelCsatSurvey();
      }
    }
    setIsOpen(open);
  }, [cancelCsatSurvey, closeHelpTooltip]);
  const {
    toggleHelpWidget
  } = useToggleHelpWidget({
    onToggleWidget
  });
  useAddZorseFunctions({
    toggleHelpWidget
  });
  useAutoOpen({
    toggleHelpWidget
  });
  useObserveHtmlClass('zorse-opened', setIsOpen);
  const onClick = () => {
    toggleHelpWidget();
    setShowNotification(false);
    trackHelpNotificationDismiss();
  };
  return /*#__PURE__*/_jsx(HelpIconTooltip, {
    shouldRenderHelpTooltip: shouldRenderHelpTooltip,
    onClose: closeHelpTooltip,
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "Global Toolbar - Help",
      children: /*#__PURE__*/_jsxs(StyledHelpButton, {
        "aria-label": t('nav.help.button'),
        "aria-haspopup": "dialog",
        "aria-owns": "in-app-help-iframe",
        "aria-expanded": isOpen,
        "data-test-id": id,
        id: id,
        ref: ref,
        tabIndex: tabIndex,
        onKeyDown: onKeyDown,
        onClick: onClick,
        onMouseEnter: prefetchHelpWidget,
        children: [/*#__PURE__*/_jsx(NewHelpIcon, {
          width: "16px",
          height: "16px",
          fill: navTheme(theme).navIconPrimary,
          title: t('nav.help.button')
        }), showNotification && /*#__PURE__*/_jsx(NotificationsIndicator, {
          "data-test-id": "help-notification-indication"
        })]
      })
    })
  });
}));